import { AskUserContentSchema } from '@littlebird/action-cards';
import { getSDK, isChatStatusBusy, type ActionCard } from '@littlebird/sdk';
import { toast } from 'sonner';

import { performAttributedSend } from '@/features/chat/chat-send';
import { resolveChatMode } from '@/features/chat/chat.hooks';
import { logError } from '@/lib/errorLogging';

import { askUserStore } from './ask-user.store';

const hasAcceptedAnswers = (card: ActionCard) =>
  card.status === 'completed' ||
  typeof card.result?.user_message_id === 'string';

export const submitAskUserAnswers = async (
  card: ActionCard,
  onComplete?: () => void
): Promise<void> => {
  const request = AskUserContentSchema.safeParse(card.content).data;
  if (!request || card.status !== 'draft' || !card.chatId) return;
  const answers = askUserStore.buildAnswers(request);
  if (!answers || !askUserStore.beginSubmit(request.request_id)) return;

  try {
    if (onComplete) {
      await onComplete();
      return;
    }
    askUserStore.markSent(request.request_id);
    const { threads } = getSDK().stores;
    await threads.ensureLoaded(card.chatId);
    const thread = threads.get(card.chatId);
    if (!thread?.isAvailableForInteraction || !thread.synced) {
      throw new Error('This chat is no longer available');
    }
    if (isChatStatusBusy(thread.status)) {
      throw new Error('Wait for the current response to finish');
    }
    if (card.status !== 'draft') return;
    const dispatch = await performAttributedSend({
      threadId: card.chatId,
      mode: resolveChatMode(card.chatId),
      requestResponseAudio: false,
      content: '',
      prepareExtras: () => ({ askUserAnswers: answers }),
    });
    if (dispatch.createdThreadId !== null) return;
    const outcome = await dispatch.sendPromise;
    if (outcome === 'failed' || outcome === 'cancelled') {
      throw new Error('Could not send your answers. Try again.');
    }
    // A card still in draft keeps showing the sent answers until sync completes it
    if (card.status !== 'draft') askUserStore.clear(request.request_id);
  } catch (error) {
    if (hasAcceptedAnswers(card)) {
      if (card.status !== 'draft') askUserStore.clear(request.request_id);
      return;
    }
    askUserStore.unmarkSent(request.request_id);
    toast.error(
      error instanceof Error ? error.message : 'Could not send your answers'
    );
    logError(error, 'Failed to send question answers', {
      context: 'AskUser',
      metadata: { cardId: card.id, requestId: request.request_id },
    });
  } finally {
    askUserStore.endSubmit(request.request_id);
  }
};
